import {
  ExportButtonProps,
  ParsedDocs,
  SidebarLayout,
} from "@stoplight/elements-core";
import { NodeType } from "@stoplight/types";
import * as React from "react";
import { Redirect, useLocation } from "react-router-dom";
import { computeAPITree, findFirstNodeSlug, isInternal } from "tools/utils";
import { ServiceNode } from "tools/oas/types";
import { Sidebar } from "../Sidebar";

type SidebarLayoutProps = {
  serviceNode: ServiceNode;
  logo?: string;
  hideTryIt?: boolean;
  hideSchemas?: boolean;
  hideInternal?: boolean;
  hideExport?: boolean;
  exportProps?: ExportButtonProps;
  tryItCredentialsPolicy?: "omit" | "include" | "same-origin";
  tryItCorsProxy?: string;
};

export const Document: React.FC<SidebarLayoutProps> = ({
  serviceNode,
  logo,
  hideTryIt,
  hideInternal,
  hideExport,
  exportProps,
  tryItCredentialsPolicy,
  tryItCorsProxy,
}) => {
  const container = React.useRef<HTMLDivElement>(null);
  const tree = React.useMemo(
    () => computeAPITree(serviceNode, { hideSchemas: true, hideInternal }),
    [serviceNode, hideInternal]
  );

  const location = useLocation();
  const { pathname } = location;
  const isRootPath = !pathname || pathname === "/";
  const node = isRootPath
    ? serviceNode
    : serviceNode.children.find((child) => child.uri === pathname);

  const layoutOptions = React.useMemo(
    () => ({
      hideTryIt: hideTryIt,
      hideExport: hideExport || node?.type !== NodeType.HttpService,
    }),
    [hideTryIt, hideExport, node]
  );

  if (!node) {
    // Redirect to the first child if node doesn't exist
    const firstSlug = findFirstNodeSlug(tree);

    if (firstSlug) {
      return <Redirect to={firstSlug} />;
    }
  }

  if (hideInternal && node && isInternal(node)) {
    return <Redirect to="/" />;
  }

  if (isRootPath) {
    return <Redirect to="/API Overview" />;
  }

  return (
    <SidebarLayout
      ref={container}
      sidebar={
        <Sidebar
          container={container}
          name={serviceNode.name}
          logo={logo}
          pathname={pathname}
          tree={tree}
        />
      }
    >
      {node && (
        <ParsedDocs
          key={pathname}
          uri={pathname}
          node={node}
          nodeTitle={node.name}
          layoutOptions={layoutOptions}
          location={location}
          exportProps={exportProps}
          tryItCredentialsPolicy={tryItCredentialsPolicy}
          tryItCorsProxy={tryItCorsProxy}
        />
      )}
    </SidebarLayout>
  );
};
